const WINDOW_NOT_FOUND = 'WINDOW_NOT_FOUND';

type WindowNotFoundDetails = {
  reason?: unknown;
  windowHandle?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as Record<string, unknown>;
};

const detailsFromError = (error: unknown): WindowNotFoundDetails | undefined => {
  const obj = asRecord(error);
  return obj ? asRecord(obj.details) : undefined;
};

export const isWindowNotFoundError = (error: unknown): boolean =>
  detailsFromError(error)?.reason === WINDOW_NOT_FOUND;

const requestFromPayload = (
  payload?: Record<string, unknown>
): Record<string, unknown> | undefined => asRecord(payload?.request);

const windowHandleFromScoped = (scoped: unknown): number | undefined => {
  const obj = asRecord(scoped);
  if (obj?.mode !== 'window') return undefined;
  return typeof obj.windowHandle === 'number' ? obj.windowHandle : undefined;
};

export const windowHandleFromWindowNotFound = (
  error: unknown,
  payload?: Record<string, unknown>
): number | undefined => {
  const details = detailsFromError(error);
  if (details?.reason !== WINDOW_NOT_FOUND) return undefined;
  if (typeof details.windowHandle === 'number') return details.windowHandle;
  const request = requestFromPayload(payload);
  return (
    windowHandleFromScoped(request?.target) ?? windowHandleFromScoped(request?.scope)
  );
};

export type RpaWindowNotFoundListener = (
  handle: number,
  generation: number | undefined
) => void;

const windowNotFoundListeners = new Set<RpaWindowNotFoundListener>();

let currentEhrPinGeneration: number | null = null;

export const reportEhrPinGeneration = (generation: number | null): void => {
  currentEhrPinGeneration = generation;
};

export const readEhrPinGeneration = (): number | undefined =>
  currentEhrPinGeneration ?? undefined;

export const subscribeRpaWindowNotFound = (
  cb: RpaWindowNotFoundListener
): (() => void) => {
  windowNotFoundListeners.add(cb);
  return () => {
    windowNotFoundListeners.delete(cb);
  };
};

export const notifyRpaWindowNotFound = (
  error: unknown,
  payload?: Record<string, unknown>,
  generation?: number
): void => {
  const handle = windowHandleFromWindowNotFound(error, payload);
  if (handle === undefined) return;
  for (const listener of [...windowNotFoundListeners]) {
    listener(handle, generation);
  }
};
